import { useAuth } from '../contexts/AuthContext';
import { canWriteCustomers, isCrmRole } from '../lib/roles';
import { isSalesRole } from '../lib/sales-access';
import { CUSTOMER_STATUSES, type CustomerStatus } from '../lib/customer-status';

/**
 * Which customer CRM controls this user may be shown — one flag per API gate,
 * the useExpensePermissions pattern. Read from fieldiq-api
 * `customers.routes.ts` and `customer-contacts.routes.ts` (api#1038):
 *
 *   POST /customers, PUT /customers/:id
 *       requireRole(...CUSTOMER_WRITE_ROLES, 'sales') — i.e. CRM_ROLES.
 *       A SALES caller may create and edit PROSPECTS ONLY: a status other than
 *       'prospect' in the body, or a PUT on a customer whose current status is
 *       not 'prospect', answers 403 `sales_prospect_only`. So for sales the
 *       status control has exactly one value and is not offered at all.
 *   POST /customers/:id/contacts
 *       CUSTOMER_WRITE_ROLES — sales is refused (operator, 2026-10-12).
 *   GET /users (the owner picker)
 *       Not on the sales guard's allowlist, so a sales user cannot list people;
 *       they are offered "Assign to me" on a prospect instead.
 *
 * This HIDES; the API enforces. A role this hook does not know is offered
 * nothing new.
 */
export interface CustomerPermissions {
  /** May open the quick-add and POST a prospect. */
  canCreateProspect: boolean;
  /** May use the full New Client form (any status). Sales may not. */
  canCreateCustomer: boolean;
  /** May create a contact row alongside a new prospect. */
  canWriteContacts: boolean;
  /** May edit this customer's fields at all. */
  canEdit: (customer: { status?: string | null } | null | undefined) => boolean;
  /** The statuses this user may move this customer to; empty = no status control. */
  statusOptions: (customer: { status?: string | null } | null | undefined) => readonly CustomerStatus[];
  /** May pick any tenant user as owner (needs GET /users). */
  canPickOwner: (customer: { status?: string | null } | null | undefined) => boolean;
  /** May set themselves as owner (sales on a prospect). */
  canAssignSelf: (customer: { status?: string | null } | null | undefined) => boolean;
  userId: string | null;
}

export function useCustomerPermissions(): CustomerPermissions {
  const { user } = useAuth();
  const role = user?.role;
  const sales = isSalesRole(role);
  const crm = isCrmRole(role);
  const office = crm && !sales; // CUSTOMER_WRITE_ROLES exactly
  const isProspect = (c: { status?: string | null } | null | undefined) => c?.status === 'prospect';

  return {
    canCreateProspect: crm,
    canCreateCustomer: office,
    canWriteContacts: canWriteCustomers(role),
    canEdit: (c) => office || (sales && isProspect(c)),
    statusOptions: (c) => (office && c ? CUSTOMER_STATUSES : []),
    canPickOwner: (c) => office && !!c,
    canAssignSelf: (c) => sales && isProspect(c),
    userId: user?.id ?? null,
  };
}
